import React, { useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, Button } from '@/Components/shadcn';
import { cn } from '@/lib/utils';
import { shortlistedStatusesOrder } from '../../../api/useShortlistedProfiles';
import UIStrings from '@/Utils/UIStrings';
import { ShortlistedStatus } from '../../../api/DTO/ShortlistedProfile';
import useDropdownPosition from '@/Hooks/useDropdownPosition';

interface ShortlistedStatusActionProps {
    onChangeCallback: (newStatus: ShortlistedStatus) => Promise<void>;
    profileStatus?: ShortlistedStatus;
}

const statuses = shortlistedStatusesOrder.map((status, index) => ({
    id: status.toString(),
    name: `${index + 1}. ${UIStrings[status]}`,
}));

const getStatusColor = (status?: ShortlistedStatus): string => {
    switch (status) {
        case 'ContactedEmail':
        case 'ContactedInMail':
        case 'ContactedPhone':
            return 'bg-blueish text-blueish-foreground';
        case 'ErrorContactManually':
        case 'ContactTaskInMail':
        case 'ContactTaskPhone':
            return 'bg-orange-100/90 text-orange-600';
        case 'RepliedEmail':
        case 'RepliedInMail':
        case 'RepliedPhone':
            return 'bg-[#e5f4ec] text-[#005833]';
        case 'NoReply':
        case 'NotInterested':
        case 'Interested':
        case 'Applied':
        case 'ContactCancelled':
            return 'bg-[#e4f0f4] text-[#1f4464]';
        case 'NotContacted':
        default:
            return 'bg-gray-100 text-gray-700';
    }
};

const ShortlistedStatusAction = ({ onChangeCallback, profileStatus }: ShortlistedStatusActionProps) => {
    const selectedLabel = useMemo(() => statuses.find((s) => s.id === profileStatus)?.name ?? '', [profileStatus]);
    const { ref, maxHeight, side } = useDropdownPosition<HTMLDivElement>({ margin: 10 });

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                {profileStatus ? (
                    <div
                        data-cy="shortlistedStatusDropdown"
                        className={cn(
                            'inline-flex h-full w-full cursor-pointer items-center gap-1 rounded-lg text-sm transition-colors',
                        )}
                        ref={ref}
                    >
                        <span
                            className={cn(
                                'overflow-hidden rounded-md p-1 text-xs font-medium text-ellipsis whitespace-nowrap',
                                getStatusColor(profileStatus),
                            )}
                        >
                            {selectedLabel}
                        </span>
                        <ChevronDown className="text-muted-foreground ml-auto h-4 w-4 shrink-0" />
                    </div>
                ) : (
                    <Button variant="outline" size="sm" data-cy="shortlistedStatusDropdown">
                        <span className="text-secondary-foreground text-sm font-bold">{UIStrings.ChangeStatus}</span>
                        <ChevronDown className="text-muted-foreground ml-auto h-4 w-4 shrink-0" />
                    </Button>
                )}
            </DropdownMenuTrigger>
            <DropdownMenuContent
                portalled
                align="start"
                className="min-w-52 overflow-y-auto"
                data-cy="shortlistedStatusMenuContent"
                style={profileStatus ? { maxHeight } : undefined}
                side={profileStatus ? side : undefined}
            >
                {statuses.map((status) => (
                    <DropdownMenuItem key={status.id} onClick={() => onChangeCallback(status.id as ShortlistedStatus)}>
                        <span
                            className={cn(
                                'rounded-md px-2 py-1 text-xs font-medium',
                                getStatusColor(status.id as ShortlistedStatus),
                            )}
                        >
                            {status.name}
                        </span>
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default ShortlistedStatusAction;
